import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";
import { handleAdminUserList } from "../../services/APIClient";
import { STATUS_CODES } from "../../utils/statusCode";
import Toster from "../../utils/Toaster";
import { removeUserData } from "../../store/slices/userSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "../../utils/CommonFunction";
import { useLoader } from "../../contexts/LoaderContext";

const OfficerList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setLoading } = useLoader();

  const [userList, setUserList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const attention = searchParams.get("attention") || "";
  const attentionLabels = {
    unactivated30d: "New officers not activated",
    inactive: "Inactive accounts",
  };

  const fetchUserList = async (page, query = "") => {
    setLoading(true);
    try {
      const response = await handleAdminUserList({
        pageNumber: page,
        search: query,
        attention,
      });

      if (response?.code === STATUS_CODES.SUCCESS) {
        setUserList(response.data.UserList || []);
        setTotalCount(response.data.userCount || 0);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to fetch content", "error");
      }
    } catch (error) {
      Toster("An error occurred while fetching user list", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchUserList(currentPage, searchQuery);
  }, [currentPage, searchQuery, attention]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setCurrentPage(1);
    }, 500),
    []
  );

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const openOfficerDetail = (id) => {
    navigate(`/officer/${id}`);
  };

  return (
    <div className="w-full px-0 py-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 px-4">
        <h1 className="text-[30px] font-semibold text-[#000] mb-4 sm:mb-0 leading-[100%]">
          {attentionLabels[attention] || "Officers List"}
        </h1>

        <div className="relative w-full sm:w-[300px]">
          <img
            src="/search.png"
            alt="Search Icon"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={handleSearch}
            aria-label="Search officers"
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-4 text-[#111827] caret-[#67A3FA] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {attentionLabels[attention] && (
        <div className="mx-4 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#67A3FA]/25 bg-[#67A3FA]/10 px-4 py-3">
          <p className="text-sm font-medium text-[#DCEBFF]">
            Showing: {attentionLabels[attention]}
          </p>
          <button
            type="button"
            onClick={() => navigate("/officers")}
            className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:border-white/30 hover:text-white"
          >
            View all officers
          </button>
        </div>
      )}

      <div className="w-full overflow-x-auto">
        <table className="min-w-[800px] w-full bg-white text-left text-sm shadow-md rounded-lg">
          <thead className="bg-[#252525] text-white sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-[16px]">Image</th>
              <th className="px-4 py-3 text-[16px]">Name</th>
              <th className="px-4 py-3 text-[16px]">Email</th>
              {/* <th className="px-4 py-3 text-[16px]">User ID</th> */}
              <th className="px-4 py-3 text-[16px]">Department name</th>
              <th className="px-4 py-3 text-[16px]">Badge number</th>
              <th className="px-4 py-3 text-[16px]">Subscription</th>
              <th className="px-4 py-3 text-[16px]">Testimony</th>
              <th className="px-4 py-3 text-[16px]">Action</th>
            </tr>
          </thead>
          <tbody>
            {userList.length > 0 ? (
              userList.map((user, index) => (
                <tr
                  key={index}
                  className="border-b"
                  style={{
                    backgroundColor: index % 2 === 1 ? "#fff" : "#F2F2F2",
                  }}
                >
                  <td className="px-4 py-3">
                    <img
                      src={user.profileImage}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  </td>
                  <td className="px-4 py-3 text-[#333333] font-medium">
                    {user.fullname}
                  </td>
                  <td className="px-4 py-3 text-[#333333] font-medium">
                    {user.email}
                  </td>
                  {/* <td className="px-4 py-3 text-[#333333] font-normal">
                    {user.userUniqueId}
                  </td> */}
                  <td className="px-4 py-3 text-[#333333] font-normal">
                    {user.departmentName}
                  </td>
                  <td className="px-4 py-3 text-[#333333] font-normal">
                    {user.badgeNumber}
                  </td>
                  <td className="px-4 py-3 text-[#333333] font-medium">
                    {user.userSubscribedPlan || "Not Subscribed"}
                  </td>
                  <td className="px-4 py-3 text-center text-[#333333] font-normal">
                    {user.totalTestimonyCount}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      className="text-blue-500 hover:text-blue-700"
                      onClick={() => openOfficerDetail(user._id)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center text-gray-500 px-4 py-6">
                  No officers available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end px-4">
        {userList.length > 0 && (
          <Pagination
            current={currentPage}
            total={totalCount}
            pageSize={10}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
          />
        )}
      </div>
    </div>
  );
};

export default OfficerList;
